import { Table } from '@/components';
import { TablePaginationComp, type PaginationConfig } from '@/components/table/TablePagination';
import type { ColumnConfig } from '@/components/table/Table';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import {
  type BillingReport,
  BillingReportStatus,
  BILLING_REPORT_STATUS_LABELS,
} from '../models/BillingReport';

const statusColor: Record<BillingReportStatus, 'warning' | 'success' | 'error'> = {
  [BillingReportStatus.PROCESSING]: 'warning',
  [BillingReportStatus.COMPLETED]: 'success',
  [BillingReportStatus.ERRORED]: 'error',
};

function formatDay(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const columns: ColumnConfig[] = [
  {
    label: 'Quincena',
    field: 'periodStart',
    render: (_value, row: BillingReport) =>
      `${formatDay(row.periodStart)} al ${formatDay(row.periodEnd)}`,
  },
  {
    label: 'Estado',
    field: 'status',
    render: (value: BillingReportStatus) => (
      <Chip
        label={BILLING_REPORT_STATUS_LABELS[value] ?? value}
        color={statusColor[value] ?? 'default'}
        size='small'
      />
    ),
  },
  {
    label: 'Filas',
    field: 'rowCount',
    render: (value: number | null) => (value == null ? '—' : value.toLocaleString('es-AR')),
  },
  {
    label: 'Generado',
    field: 'finishedAt',
    render: (value: string | null) => formatDateTime(value),
  },
];

interface BillingReportsTableProps {
  reports: BillingReport[];
  isLoading: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  downloadingId: string | null;
  onPageChange: (page: number) => void;
  onDownload: (report: BillingReport) => void;
}

const BillingReportsTable = ({
  reports,
  isLoading,
  totalItems,
  currentPage,
  pageSize,
  downloadingId,
  onPageChange,
  onDownload,
}: BillingReportsTableProps) => {
  const pagination: PaginationConfig = {
    totalItems,
    totalPages: Math.max(1, Math.ceil(totalItems / pageSize)),
    currentPage,
    rowsPerPage: pageSize,
    onPageChange,
    onRowsPerPageChange: () => {},
    rowsPerPageOptions: [pageSize],
  };

  const renderActions = (report: BillingReport) => {
    const ready = report.status === BillingReportStatus.COMPLETED;
    return (
      <Tooltip title={ready ? 'Descargar Excel' : 'Todavía no está disponible'}>
        <span>
          <IconButton
            size='small'
            disabled={!ready || downloadingId === report.id}
            onClick={() => onDownload(report)}
          >
            <DownloadIcon fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <div className='bg-white rounded-lg shadow-md overflow-x-auto'>
      <Table
        columns={columns}
        data={reports}
        isLoading={isLoading}
        skeletonRows={6}
        renderActions={renderActions}
        getRowKey={(row) => row.id}
      />
      <TablePaginationComp {...pagination} />
    </div>
  );
};

export default BillingReportsTable;
